import { getOryFrontend } from '../ory/client';
import type { LoginFlow, ContinueWith } from '@ory/client';
import { resetAuthSession } from '../utils/authReset';
interface UseLoginPayload {
  onSuccess: () => void;
  onError: () => void;
}

// Opens the login flow in a popup window. On completion, the callback page
// notifies the opener via postMessage for the app to continue.
export const useGoogleLoginHandler = ({ onSuccess, onError }: UseLoginPayload) => {
  const handleLogin = async () => {
    try {
      const origin = window.location.origin;
      const returnTo = `${origin}/auth/login-complete`;
      const url = `${origin}/self-service/login/browser?return_to=${encodeURIComponent(returnTo)}`;

      const passwordSignupDone =
        window.sessionStorage.getItem('last_flow_origin') === 'password-signup';
      if (passwordSignupDone) {
        await resetAuthSession({ mode: 'popup', returnTo: '/' });
      }

      // If already inside the popup (flow=... present), submit OIDC to start Google.
      const params = new URLSearchParams(window.location.search);
      const flow = params.get('flow');
      if (flow) {
        // Fetch the login flow to retrieve the OIDC Google link or form action, then navigate/submit.
        (async () => {
          try {
            // Prevent infinite OIDC re-attempt loops in the popup when Google returns and
            // Kratos bounces back to /?mode=login repeatedly due to duplicate email.
            try {
              const key = 'oidc_google_attempts';
              const attempts = parseInt(window.sessionStorage.getItem(key) || '0', 10) || 0;
              if (attempts >= 1) {
                // We've already tried once in this popup and got redirected back to a new flow.
                // Treat this as a link-required situation.
                if (window.opener && window.opener !== window) {
                  window.opener.postMessage({ type: 'AUTH_LINK_REQUIRED' }, origin);
                }
                try {
                  window.close();
                } catch {}
                return;
              }
              window.sessionStorage.setItem(key, String(attempts + 1));
            } catch {}

            const ory = getOryFrontend();
            const { data } = await ory.getLoginFlow({ id: flow });

            // 0) Detect duplicate/exists errors which indicate account linking is required
            const flowMsgs: any[] = ((data as any)?.ui?.messages || []).concat(
              ...(((data as any)?.ui?.nodes || []).map((n: any) => n?.messages || []) as any[])
            );
            const msgText = (flowMsgs || [])
              .flat()
              .map((m: any) => (m?.text || m)?.toString().toLowerCase())
              .join(' | ');
            const looksLikeDuplicate =
              msgText.includes('already exists') ||
              msgText.includes('already registered') ||
              msgText.includes('duplicate key') ||
              msgText.includes('verifiable address') ||
              msgText.includes('identifier belongs to another account');
            if (looksLikeDuplicate) {
              try {
                if (window.opener && window.opener !== window) {
                  window.opener.postMessage({ type: 'AUTH_LINK_REQUIRED' }, origin);
                }
              } catch {}
              try {
                window.close();
              } catch {}
              return;
            }

            // 1) Prefer continue_with transitions if available
            const cw: ContinueWith[] = (data as any)?.continue_with || [];
            const googleCw = cw.find(
              (c: any) =>
                c?.action === 'oidc' &&
                (/google/i.test((c as any)?.provider || '') ||
                  /google/i.test((c as any)?.provider_id || ''))
            );
            if ((googleCw as any)?.href) {
              window.location.href = (googleCw as any).href as string;
              return;
            }

            // 2) Look for a node with an href to follow
            const nodes = (data as LoginFlow)?.ui?.nodes || [];
            const hrefNode = nodes.find(
              (n: any) =>
                n?.group === 'oidc' &&
                n?.attributes?.href &&
                (/google/i.test(n?.meta?.label?.text || '') ||
                  /google/i.test(n?.attributes?.value || ''))
            );
            if ((hrefNode as any)?.attributes?.href) {
              window.location.href = (hrefNode as any).attributes.href as string;
              return;
            }

            // 3) Submit the flow form with method=oidc&provider=google&csrf_token
            // Read CSRF token from nodes if present (required when triggering OIDC via API)
            const csrfNode = (data as LoginFlow)?.ui?.nodes?.find(
              (n: any) => n?.attributes?.name === 'csrf_token' && n?.attributes?.value
            ) as any;
            const csrf_token = csrfNode?.attributes?.value as string | undefined;

            try {
              const resp = await ory.updateLoginFlow({
                flow,
                updateLoginFlowBody: {
                  method: 'oidc',
                  provider: 'google',
                  ...(csrf_token ? { csrf_token } : {}),
                } as any,
              });
              const upd = (resp as any)?.data || {};
              // Some versions respond with a redirect target to continue the browser flow
              const redirect = upd?.redirect_browser_to || upd?.redirect_to;
              if (redirect) {
                window.location.href = redirect;
                return;
              }
              const cont = upd?.continue_with || [];
              const cwGoogle = cont.find(
                (c: any) =>
                  c?.action === 'oidc' &&
                  (/google/i.test((c as any)?.provider || '') ||
                    /google/i.test((c as any)?.provider_id || ''))
              );
              if ((cwGoogle as any)?.href) {
                window.location.href = (cwGoogle as any).href as string;
                return;
              }
              // As last resort, try to follow any oidc href node
              const updHrefNode = upd?.ui?.nodes?.find(
                (n: any) => n?.group === 'oidc' && n?.attributes?.href
              );
              if ((updHrefNode as any)?.attributes?.href) {
                window.location.href = (updHrefNode as any).attributes.href as string;
                return;
              }
            } catch (err: any) {
              // Kratos may return 422 BrowserLocationChangeRequired with a redirect target
              const rdata = err?.response?.data || {};
              const redirect = rdata?.redirect_browser_to || rdata?.redirect_to;
              if (redirect) {
                window.location.href = redirect;
                return;
              }
              const cont = rdata?.continue_with || [];
              const cwGoogle = cont.find(
                (c: any) =>
                  c?.action === 'oidc' &&
                  (/google/i.test((c as any)?.provider || '') ||
                    /google/i.test((c as any)?.provider_id || ''))
              );
              if ((cwGoogle as any)?.href) {
                window.location.href = (cwGoogle as any).href as string;
                return;
              }
              const nodesErr = rdata?.ui?.nodes || [];
              const hrefNodeErr = nodesErr.find(
                (n: any) => n?.group === 'oidc' && n?.attributes?.href
              );
              if ((hrefNodeErr as any)?.attributes?.href) {
                window.location.href = (hrefNodeErr as any).attributes.href as string;
                return;
              }
              throw err; // rethrow to trigger onError below
            }
          } catch (e) {
            console.error('OIDC start error', e);
            try {
              if (window.opener && window.opener !== window) {
                window.opener.postMessage({ type: 'AUTH_LOGIN_ERROR' }, origin);
              }
            } catch {}
            try {
              window.close();
            } catch {}
            onError();
          }
        })();
        return;
      }

      const width = 500;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      const popup = window.open(
        url,
        'Txt2create Login',
        `width=${width},height=${height},left=${left},top=${top},resizable,scrollbars`
      );

      if (!popup) {
        // Popup blocked - fallback to full redirect
        window.location.href = url;
        return;
      }

      const onMessage = (event: MessageEvent) => {
        try {
          if (event.origin !== origin) return;
          if (event.data?.type === 'AUTH_LOGIN_DONE') {
            window.removeEventListener('message', onMessage);
            try {
              // Some COOP policies block close(); attempt to neutralize first
              popup.location.replace('about:blank');
              popup.close();
            } catch {}
            onSuccess();
          } else if (event.data?.type === 'AUTH_LINK_REQUIRED') {
            window.removeEventListener('message', onMessage);
            try {
              popup.location.replace('about:blank');
              popup.close();
            } catch {}
            // Redirect to login page with a hint; consumer can show guidance
            try {
              sessionStorage.setItem('auth_link_required', 'google');
            } catch {}
            window.location.href = '/?mode=login&link_required=1';
            onError();
          } else if (event.data?.type === 'AUTH_LOGIN_ERROR') {
            window.removeEventListener('message', onMessage);
            try {
              popup.location.replace('about:blank');
              popup.close();
            } catch {}
            onError();
          }
        } catch (e) {
          console.error('Login message handler error', e);
        }
      };

      window.addEventListener('message', onMessage);
    } catch (e) {
      console.error('Failed to start login flow', e);
      onError();
    }
  };

  const isLoginButtonDisabled = window?.location?.hostname === 'dev.txt2create.com';

  return { handleLogin, isLoginButtonDisabled };
};

export default useGoogleLoginHandler;
